<template>
  <div class="action-panel product-messages-panel" data-panel="seller">
    <button data-close-panel>Close ✕</button>

    <div class="action-panel__content">
      <MessageThread
        ref="thread"
        :messages="localMessages"
        :isSending="isSending"
        :meAvatar="meAvatar"
        @send="sendMessage"
        @like="onLike"
        @unlike="onUnlike"
      />
    </div>
  </div>
</template>

<script>
import MatchmakingService from '@/services/MatchmakingService';
import MessageThread from '@/components/matchmaking/MessageThread.vue';

export default {
  name: 'ProductMessagesPanel',

  components: {
    MessageThread,
  },

  props: {
    productId: {
      type: [Number, String],
      required: true,
    },
    messages: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localMessages: [],
      isSending: false,
    };
  },

  computed: {
    me() {
      return this.$store.getters.getMe || {};
    },

    meAvatar() {
      return this.me?.avatar || '';
    },
  },

  watch: {
    messages: {
      immediate: true,
      handler(val) {
        this.localMessages = Array.isArray(val) ? [...val] : [];
      },
    },
  },

  methods: {
    async scrollToLastMessage() {
      const { thread } = this.$refs;
      if (thread && typeof thread.scrollToLastMessage === 'function') {
        await thread.scrollToLastMessage();
      }
    },

    async sendMessage(text) {
      if (!text) return;

      this.isSending = true;
      try {
        const payload = {
          product_id: this.productId,
          text,
        };

        const { data } = await MatchmakingService.marketplaceProductMessage(payload);

        // Backend usually returns the created message.
        this.localMessages.push(data);
        this.$emit('sent', data);
      } catch (e) {
        // Fallback: optimistic message so UI still works even if endpoint differs.
        const optimistic = {
          id: `tmp-${Date.now()}`,
          text,
          created_at: new Date().toISOString(),
          user: {
            name: this.me?.name || 'Me',
            avatar: this.me?.avatar || '',
            role: this.me?.role || 'seller',
          },
          __optimistic: true,
        };

        this.localMessages.push(optimistic);
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.isSending = false;
      }
    },

    async onLike(msg) {
      if (!msg?.id) return;
      try {
        await MatchmakingService.addLikeToMessage(msg.id);
        // eslint-disable-next-line no-underscore-dangle
        const m = this.localMessages.find((x) => x.id === msg.id || x.id === msg._key);
        if (m) {
          this.$set(m, 'liked', true);
          this.$set(m, 'is_liked', true);
          const cnt = Number(m?.likes_count ?? m?.likes ?? 0) + 1;
          this.$set(m, 'likes_count', cnt);
          this.$set(m, 'likes', cnt);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Like failed:', e);
      }
    },

    async onUnlike(msg) {
      if (!msg?.id) return;
      try {
        await MatchmakingService.removeLikeFromMessage(msg.id);
        // eslint-disable-next-line no-underscore-dangle
        const m = this.localMessages.find((x) => x.id === msg.id || x.id === msg._key);
        if (m) {
          this.$set(m, 'liked', false);
          this.$set(m, 'is_liked', false);
          const cnt = Math.max(0, Number(m?.likes_count ?? m?.likes ?? 1) - 1);
          this.$set(m, 'likes_count', cnt);
          this.$set(m, 'likes', cnt);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Unlike failed:', e);
      }
    },
  },
};
</script>

<style scoped>
.product-messages-panel {
  --panel-margin-left: 0;
  flex: 0 0 100%;
  width: 100%;
  max-width: 100%;
  margin-left: 0;
  box-sizing: border-box;
}
</style>
